import React from 'react';
import { TI_CHANNELS, TI_PRIORITIES, TI_TRIGGERED_BY } from '../../triage/config';
import { getTriageCurrentUserId } from '../../triage/identity';
import { tiItemKind, tiStaffById } from '../../triage/utils';
import type { TriageItem } from '../../triage/types';
import { Icon } from '../Icon/Icon';
import { TriageAssigneeAvatar } from './TriageAssigneeAvatar';
import { TriageStatusPill } from './TriageStatusPill';

export interface TriageListRowProps {
  item: TriageItem;
  isSelected: boolean;
  onSelect: () => void;
  index?: number;
}

export function TriageListRow({ item, isSelected, onSelect, index = 0 }: TriageListRowProps) {
  const kind = tiItemKind(item);
  const isCall = kind === 'call_task';
  const ch = TI_CHANNELS[item.channel];
  const trig = item.triggeredBy ? TI_TRIGGERED_BY[item.triggeredBy] : null;
  const pr = TI_PRIORITIES[item.priority] ?? TI_PRIORITIES.medium!;
  const isUrgentOpen = item.status === 'open' && item.priority === 'urgent';
  const assignee = tiStaffById(item.assigneeId);
  const isMine = item.assigneeId === getTriageCurrentUserId();
  const isClosed = item.status === 'closed';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`ti-list-row v6-fade-up v6-d-${Math.min(index, 5)} w-full shrink-0`}
      style={{
        padding: '12px 14px',
        borderRadius: 10,
        marginBottom: 5,
        width: '100%',
        boxSizing: 'border-box',
        border: isSelected
          ? '1px solid color-mix(in srgb, var(--success) 80%, #000)'
          : isUrgentOpen
            ? '1px solid var(--terracotta-tint)'
            : '1px solid var(--cream-2)',
        background: isSelected
          ? 'color-mix(in srgb, var(--success) 7%, transparent)'
          : isClosed
            ? 'color-mix(in srgb, var(--cream-1) 62%, var(--cream-0))'
            : isUrgentOpen
              ? 'rgba(168, 68, 46, 0.04)'
              : 'var(--cream-1)',
        boxShadow: isSelected ? '0 0 0 1px var(--success-tint)' : 'var(--sh-1)',
        opacity: isClosed ? 0.75 : 1,
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6, flexWrap: 'wrap' }}>
        {isCall ? (
          <span
            className="ti-channel-chip"
            style={{ background: 'var(--success-tint)', color: 'color-mix(in srgb, var(--success) 80%, #000)' }}
          >
            <Icon kind="phone" size={10} stroke={2.2} />
            Call
          </span>
        ) : ch ? (
          <span className="ti-channel-chip" style={{ background: ch.bg, color: ch.color }}>
            <Icon kind={ch.icon} size={10} stroke={2.2} />
            {ch.shortLabel}
          </span>
        ) : null}
        {trig ? (
          <span className="ti-channel-chip" style={{ background: trig.bg, color: trig.color }}>
            <Icon kind={trig.icon} size={10} stroke={2.2} />
            {trig.label}
          </span>
        ) : null}
        {!isClosed ? (
          <span className={`v6-pill ${pr.pillClass}`} style={{ fontSize: 10, padding: '1px 7px', fontWeight: 700 }}>
            {item.priority === 'urgent' ? (
              <span className="ti-urgent-dot" style={{ width: 5, height: 5, animation: 'none' }} />
            ) : null}
            {pr.label}
          </span>
        ) : null}
        <TriageStatusPill status={item.status} size="small" />
        <span className="v6-mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-3)' }}>
          {item.time}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: 'var(--ink-0)',
              letterSpacing: '-0.005em',
              marginBottom: 2,
            }}
          >
            {item.patient}
          </div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink-1)',
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.005em',
            }}
          >
            {item.subject}
          </div>
        </div>
        <div title={assignee ? `${assignee.name} · ${assignee.role}` : 'Unassigned'}>
          <TriageAssigneeAvatar staff={assignee} size={24} showRing={isMine} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {item.tags.map((tag) => (
          <span key={tag} className="ti-tag">
            {tag}
          </span>
        ))}
        {isMine ? (
          <span
            style={{
              fontSize: 9.5,
              fontWeight: 600,
              color: 'var(--ai-3)',
              background: 'var(--ai-tint)',
              padding: '2px 6px',
              borderRadius: 4,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            Mine
          </span>
        ) : null}
      </div>
    </div>
  );
}
